import React, { useState } from 'react';
import notifyHB from '../../config/notifyHB';
import { toast } from '@/components/toast/toast';
import styles from './form-summary.module.css';
import { sendOnboardingOtp } from '../../use-cases/send-onboarding-otp';
import { Button } from '@/components/ui/button';
import { Loader2Icon, Pencil } from 'lucide-react';
import { CheckboxLabel } from '@/components/checkbox-label/checkbox-label';
import { getUserEmail } from '@/utils/get-user-email';
import { isHttpError } from '@/http';
import { isClientHttpError } from '@/utils/is-client-http-error';
import { getErrorReason, trackEvent } from '@/utils/analytics';

import { PersonalInfoFormData } from './PersonalInfo';

interface FormSummaryProps {
	personName: string;
	address: string;
	userPhoneCountryCode: string;
	userPhone: string;
	nit: string;
	userEmail: string;
	documentNumber: string;
	setComponentState: (newState: number) => void;
	nextStep: () => void;
	prevStep: () => void;
	certificateType?: string;
	additionalData?: PersonalInfoFormData;
}

const FormSummary: React.FC<FormSummaryProps> = ({
	nextStep,
	prevStep,
	personName,
	address,
	userPhoneCountryCode,
	userPhone,
	nit,
	userEmail,
	documentNumber,
	setComponentState,
	certificateType,
	additionalData,
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [checkBoxValue, setCheckBoxValue] = useState(false);
	const [fieldsWarning, setFieldsWarning] = useState(<p></p>);

	const confirmStep = () => {
		const onboardingUlid: string =
			localStorage.getItem('onboarding-ulid') || '';
		setIsLoading(true);
		sendOnboardingOtp(onboardingUlid)
			.then((response) => {
				if ([200, 201, 202].includes(response.status)) {
					nextStep();
				} else {
					toast({ mode: 'destructive', description: 'Error al enviar el otp' });
					trackEvent('onboarding_error', {
						step: 'summary',
						reason: 'sms_send_unexpected_status',
					});
				}
			})
			.catch((err: unknown) => {
				if (
					isHttpError(err) &&
					(err.response?.data as { error?: string } | null)
						?.error === 'We already have this users data'
				) {
					localStorage.removeItem('onboard');
					nextStep();
				} else if (isClientHttpError(err)) {
					toast({ mode: 'destructive', description: 'Error al enviar el otp' });
					trackEvent('onboarding_error', {
						step: 'summary',
						reason: `sms_send_${getErrorReason(err)}`,
					});
					notifyHB('request-signature', 'CheckIdentity', err);
				} else {
					trackEvent('onboarding_error', {
						step: 'summary',
						reason: `sms_send_${getErrorReason(err)}`,
					});
					setFieldsWarning(
						<p style={{ color: 'red' }}>
							Hubo un error con la solicitud, por favor verificar
							que la información personal sea la correcta.
						</p>,
					);

					notifyHB('request-signature', 'CheckIdentity', err);
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	/**
	 * Returns the human-readable label for a given certificate type.
	 */
	const getCertificateTypeLabel = (type: string) => {
		switch (type) {
			case 'natural':
				return 'Persona individual';
			case 'legal_representative':
				return 'Representante legal';
			case 'company':
				return 'Persona jurídica';
			case 'employee':
				return 'Empleado de persona jurídica';
			case 'professional':
				return 'Profesional colegiado';
			case 'public_official':
				return 'Funcionario público';
			default:
				return type;
		}
	};

	const goToStep2 = () => setComponentState(2);

	/**
	 * Renders the additional fields based on certificate type.
	 */
	const renderAdditionalData = () => {
		if (!certificateType || !additionalData) return null;

		let fields: { label: string; value: string }[] = [];

		switch (certificateType) {
			case 'legal_representative': {
				const legalRep = additionalData.legalRepInfo;
				fields = [
					{
						label: 'Nombre de persona jurídica',
						value: legalRep?.company_name,
					},
					{
						label: 'NIT de persona jurídica',
						value: legalRep?.company_tax_id,
					},
					{
						label: 'Dirección de persona jurídica',
						value: legalRep?.company_address,
					},
					{
						label: 'Capacidad de representación',
						value: legalRep?.representation_capacity,
					},
				];
				break;
			}
			case 'professional': {
				const profInfo = additionalData.professionalInfo;
				fields = [
					{
						label: 'No. de colegiado',
						value: profInfo?.license_number,
					},
					{
						label: 'Colegio profesional',
						value: profInfo?.professional_association,
					},
				];
				break;
			}
			case 'employee': {
				const empInfo = additionalData.employeeInfo;
				fields = [
					{ label: 'Empresa', value: empInfo?.company_name },
					{
						label: 'Departamento',
						value: empInfo?.company_department,
					},
					{ label: 'Dirección', value: empInfo?.company_address },
					{ label: 'NIT', value: empInfo?.company_tax_id },
					{ label: 'Puesto', value: empInfo?.job_role },
				];
				break;
			}
			case 'company': {
				const compInfo = additionalData.companyInfo;
				fields = [
					{
						label: 'Nombre de persona jurídica',
						value: compInfo?.company_name,
					},
					{
						label: 'Departamento',
						value: compInfo?.company_department,
					},
					{ label: 'Dirección', value: compInfo?.company_address },
					{
						label: 'NIT de persona jurídica',
						value: compInfo?.company_tax_id,
					},
				];
				break;
			}
			case 'public_official': {
				const pubInfo = additionalData.publicOfficialInfo;
				fields = [
					{
						label: 'Organización',
						value: pubInfo?.organization_name,
					},
					{
						label: 'Dirección de la organización',
						value: pubInfo?.organization_address,
					},
					{
						label: 'Teléfono de la organización',
						value: pubInfo?.organization_phone,
					},
					{ label: 'Puesto', value: pubInfo?.position },
				];
				break;
			}
			default:
				break;
		}

		return (
			<>
				<div className={styles.field}>
					<div className={styles['field-name']}>
						Tipo de certificado
					</div>
					<div className={styles['field-value']}>
						{getCertificateTypeLabel(certificateType)}
						<Button onClick={goToStep2} size="sm" variant="ghost">
							<Pencil />
						</Button>
					</div>
				</div>
				{fields.map((field) => (
					<div className={styles.field} key={field.label}>
						<div className={styles['field-name']}>
							{field.label}
						</div>
						<div className={styles['field-value']}>
							{field.value}
							<Button
								onClick={goToStep2}
								size="sm"
								variant="ghost"
							>
								<Pencil />
							</Button>
						</div>
					</div>
				))}
			</>
		);
	};

	return (
		<div className={styles['form-summary']}>
			<div className={styles.title}>Resumen del formulario</div>
			<div className={styles.content}>
				<div className={styles.field}>
					<div className={styles['field-name']}>Nombre</div>
					<div
						className={styles['field-value']}
						style={{ textTransform: 'capitalize' }}
					>
						{personName.toLowerCase()}
						<Button onClick={goToStep2} size="sm" variant="ghost">
							<Pencil />
						</Button>
					</div>
				</div>
				<div className={styles.field}>
					<div className={styles['field-name']}>Dirección</div>
					<div className={styles['field-value']}>
						{address}
						<Button onClick={goToStep2} size="sm" variant="ghost">
							<Pencil />
						</Button>
					</div>
				</div>
				<div className={styles.field}>
					<div className={styles['field-name']}>
						Número de Identificación Personal DPI
					</div>
					<div className={styles['field-value']}>
						{documentNumber}
						<Button onClick={goToStep2} size="sm" variant="ghost">
							<Pencil />
						</Button>
					</div>
				</div>
				<div className={styles.field}>
					<div className={styles['field-name']}>NIT</div>
					<div className={styles['field-value']}>
						{nit}
						<Button onClick={goToStep2} size="sm" variant="ghost">
							<Pencil />
						</Button>
					</div>
				</div>
				<div className={styles.field}>
					<div className={styles['field-name']}>Teléfono</div>
					<div className={styles['field-value']}>
						+{userPhoneCountryCode} {userPhone}
						<Button onClick={goToStep2} size="sm" variant="ghost">
							<Pencil />
						</Button>
					</div>
				</div>
				<div className={styles.field}>
					<div className={styles['field-name']}>Correo</div>
					<div className={styles['field-value']}>
						{userEmail || getUserEmail()}
					</div>
				</div>
				{renderAdditionalData()}
			</div>

			<CheckboxLabel
				label={
					'Declaro que los datos que ingrese son correctos y que solicito se genere un certificado con los mismos.'
				}
				checked={checkBoxValue}
				onCheckedChange={setCheckBoxValue}
			/>
			{fieldsWarning}
			<div className={styles.footer}>
				<Button
					variant="secondary"
					onClick={prevStep}
					disabled={isLoading}
				>
					Regresar
				</Button>

				<Button
					disabled={!checkBoxValue || isLoading}
					onClick={confirmStep}
				>
					{isLoading && <Loader2Icon className="animate-spin" />}
					Continuar
				</Button>
			</div>
		</div>
	);
};

export default FormSummary;
